import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { getBiletlar, type BiletSummary } from '@/api';
import { ErrorNotice } from '@/components/ErrorNotice';
import { MainAction } from '@/components/MainAction';
import { usePullRefresh } from '@/components/PullToRefresh';
import { Chip, CHIPROW } from '@/components/Chip';
import { BackLink } from '@/components/BackLink';
import { Notice } from '@/components/Notice';
import { useAsync, useRefreshable } from '@/hooks';
import { useLang } from '@/lang';
import { cn } from '@/lib/utils';
import { plural, biletLabel, useT, type Strings } from '@/str';
import { DASHED, H_PAGE, MUTED, SCREEN, SUB } from '@/components/layout';

const NOTCHES = [10, 25, 50];

const BILET_NEW_FROM = 51;

type RowState = 'passed' | 'failed' | 'next' | 'new';

type Filter = 'all' | 'new' | 'done' | 'wrong';

const isNewBilet = (b: BiletSummary) => b.number >= BILET_NEW_FROM;

function subLine(state: RowState, b: BiletSummary, t: Strings): string {
  const count = `${b.question_count} ${plural(b.question_count, t.r1)}`;
  if (state === 'next') return `${count} · ${t.nextBilet}`;
  if (state === 'new') return `${count} · ${t.biletNew}`;
  return `${count} · ${state === 'passed' ? t.pass : t.fail}`;
}

const BILET =
  'anim-rise group flex w-full items-center gap-3.25 rounded-plate border border-border bg-card px-3.5 py-3 text-left text-foreground [content-visibility:auto] [contain-intrinsic-size:auto_70px] hover:bg-muted active:bg-muted active:transform-none data-[lit]:border-[1.5px] data-[lit]:border-accent-ink';
const BILET_NEW = 'bg-transparent text-muted-foreground';
const BILET_NEXT = 'border-[1.5px] border-primary';
const BILET_PLATE =
  'flex size-10.5 flex-none items-center justify-center rounded-ctl bg-muted text-t16 font-extrabold tabular-nums text-foreground group-data-[status=new]:border-[1.5px] group-data-[status=new]:border-border-strong group-data-[status=new]:bg-transparent group-data-[status=new]:text-faint group-data-[status=next]:bg-primary group-data-[status=next]:text-primary-foreground';
const BILET_NEW_TAG =
  'flex-none rounded-full border border-acc-rim bg-[rgba(255,212,0,0.14)] px-1.75 pt-[3.5px] pb-[3px] text-t10h font-extrabold uppercase leading-none tracking-kicker text-accent-ink';

function BiletSkeleton({ slow }: { slow: string }) {
  const [late, setLate] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setLate(true), 3000);
    return () => clearTimeout(id);
  }, []);
  return (
    <div aria-busy className="flex flex-col gap-2">
      {[
        ['38%', '56%'],
        ['44%', '48%'],
        ['34%', '60%'],
        ['40%', '52%'],
        ['36%', '58%'],
        ['42%', '50%'],
      ].map(([w1, w2], i) => (
        <div
          key={i}
          className="bg-card border-border rounded-plate flex items-center gap-3 border px-3.5 py-3"
        >
          <span data-skel className="rounded-ctl size-10.5 flex-none" />
          <span className="flex min-w-0 flex-1 flex-col gap-1.75">
            <span data-skel className="block h-2.75" style={{ width: w1 }} />
            <span data-skel className="block h-2.25" style={{ width: w2 }} />
          </span>
        </div>
      ))}
      {late && (
        <div
          role="status"
          aria-live="polite"
          className="text-t12 text-faint px-0 pt-2.5 pb-1 text-center font-bold text-pretty"
        >
          {slow}
        </div>
      )}
    </div>
  );
}

export function BiletlarPage() {
  const navigate = useNavigate();
  const t = useT();
  const { lang } = useLang();
  const [nonce, refresh] = useRefreshable();
  const { ref, pill } = usePullRefresh(refresh);
  const state = useAsync(getBiletlar, [nonce], { keep: true });
  const [filter, setFilter] = useState<Filter>('all');
  const focus = (useLocation().state as { focus?: number } | null)?.focus ?? null;
  const [lit, setLit] = useState<number | null>(focus);
  const listed = !state.loading && state.error === null;

  useEffect(() => {
    if (lit === null || !listed) return;
    document.querySelector(`[data-bilet="${lit}"]`)?.scrollIntoView({ block: 'center' });
    const id = setTimeout(() => setLit(null), 2200);
    return () => clearTimeout(id);
  }, [lit, listed]);

  const header = (
    <>
      <BackLink label={t.back} onClick={() => navigate('/')} />
      <h1 className={H_PAGE}>{t.biletlar}</h1>
    </>
  );

  if (state.loading)
    return (
      <div data-screen className={SCREEN}>
        <div>{header}</div>
        <BiletSkeleton slow={t.exp.slow} />
      </div>
    );
  if (state.error !== null)
    return <ErrorNotice error={state.error} onRetry={() => void refresh().catch(() => {})} />;

  const biletlar = state.data.data;
  if (biletlar.length === 0) return <Notice sign="—" title={t.noBilets} />;
  const total = state.data.count;
  const done = biletlar.filter((b) => b.status !== 'new').length;
  const pct = total === 0 ? 0 : Math.round((done / total) * 100);
  const nextNumber = biletlar.find((b) => b.status === 'new')?.number ?? null;
  const perBilet = biletlar[0]?.question_count ?? null;

  const matches: Record<Filter, (b: BiletSummary) => boolean> = {
    all: () => true,
    new: isNewBilet,
    done: (b) => b.status === 'passed',
    wrong: (b) => b.status === 'failed',
  };
  const chips: { id: Filter; label: string; count: number }[] = [
    { id: 'all', label: t.fAll, count: total },
    { id: 'new', label: t.fNew, count: biletlar.filter(matches.new).length },
    { id: 'done', label: t.fDone, count: biletlar.filter(matches.done).length },
    { id: 'wrong', label: t.fWrong, count: biletlar.filter(matches.wrong).length },
  ];
  const shown = biletlar.filter(matches[filter]);

  return (
    <div data-screen className={SCREEN} ref={ref}>
      {pill}
      <div>
        {header}
        <div className={SUB}>
          {done} / {total} {t.biletDoneSub}
          {perBilet !== null && ` · ${perBilet} ${plural(perBilet, t.r1)}`}
        </div>
        <div className="mt-3 flex flex-col gap-1.5">
          <div className="rounded-bar bg-muted relative h-2 overflow-hidden" aria-hidden>
            <div className="bg-primary h-2" style={{ width: `${pct}%` }} />
            {NOTCHES.filter((n) => n < total).map((n) => (
              <span
                key={n}
                className="bg-background absolute inset-y-0 w-0.5"
                style={{ left: `${((n / total) * 100).toFixed(1)}%` }}
              />
            ))}
          </div>
          <div className="text-faint text-t12 flex justify-between font-semibold">
            <span className="tabular-nums">{pct}%</span>
            <span>
              {total - done} {plural(total - done, t.left)}
            </span>
          </div>
        </div>
      </div>

      <div className={CHIPROW}>
        {chips.map((c) => (
          <Chip
            key={c.id}
            on={c.id === filter}
            label={c.label}
            count={c.count}
            onClick={() => setFilter(c.id)}
          />
        ))}
      </div>

      {shown.length === 0 && (
        <div className={DASHED}>
          <p className={cn(MUTED, 'm-0')}>{t.biletEmpty}</p>
        </div>
      )}

      <div className="flex flex-col gap-2">
        {shown.map((b, i) => {
          const rowState: RowState =
            b.status === 'passed'
              ? 'passed'
              : b.status === 'failed'
                ? 'failed'
                : b.number === nextNumber
                  ? 'next'
                  : 'new';
          const attempted = rowState === 'passed' || rowState === 'failed';
          return (
            <button
              key={b.number}
              type="button"
              data-bilet={b.number}
              data-lit={b.number === lit ? '' : undefined}
              data-status={rowState}
              className={cn(
                BILET,
                rowState === 'new' && BILET_NEW,
                rowState === 'next' && BILET_NEXT,
              )}
              style={{ animationDelay: `${(Math.min(i, 12) * 0.03).toFixed(2)}s` }}
              onClick={() => navigate(`/practice/bilet/${b.number}`)}
            >
              <span className={BILET_PLATE}>{b.number}</span>
              <span className="flex min-w-0 flex-1 flex-col gap-[3px]">
                <span className="flex min-w-0 items-center gap-2">
                  <span className="text-t14h font-bold tracking-[-0.01em] group-data-[status=next]:font-extrabold">
                    {biletLabel(b.number, lang)}
                  </span>
                  {isNewBilet(b) && (
                    <span data-new className={BILET_NEW_TAG}>
                      {t.newTag}
                    </span>
                  )}
                </span>
                <span className="text-faint group-data-[status=next]:text-accent-ink text-t12 flex items-center gap-[5px] font-semibold tabular-nums">
                  <span>{subLine(rowState, b, t)}</span>
                  {attempted && (
                    <span
                      className={cn(
                        'text-t10h flex-none leading-none font-extrabold',
                        rowState === 'passed' ? 'text-ok' : 'text-bad',
                      )}
                      aria-hidden
                    >
                      {rowState === 'passed' ? '✓' : '✕'}
                    </span>
                  )}
                </span>
              </span>
              {attempted ? (
                <span className="flex flex-none items-baseline gap-px tabular-nums">
                  <b className="text-foreground text-t16 font-extrabold tracking-[-0.02em]">
                    {b.best_score ?? 0}
                  </b>
                  <span className="text-faint text-t12 font-bold">/{b.question_count}</span>
                </span>
              ) : (
                <span className="text-faint text-t15 flex-none font-semibold" aria-hidden>
                  →
                </span>
              )}
            </button>
          );
        })}
      </div>

      <MainAction text={t.start} onClick={() => navigate('/practice')} />
    </div>
  );
}
